import React, {useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {Constants} from '../helpers';
import Loader from './Loader';
import {getClientsphereWidgetId} from '../requests/queries/auth';

const SCRIPT_URL = 'https://app.clientsphere.io/embed/chat-widget.js';
const CONTAINER_ID = 'clientsphere-widget-container';
const WIDGET_INNER_ID = 'chat-widget-container';
const STYLE_ID = 'clientsphere-widget-constrain';

function ClientSphereWidget() {
  const location = useLocation();
  const scriptInjected = useRef(false);
  const [widgetId, setWidgetId] = useState(null);
  const [widgetReady, setWidgetReady] = useState(false);
  const [widgetIdError, setWidgetIdError] = useState(false);
  const {user, userDetails} = useSelector(state => state?.auth || {});

  const isSupport = location.pathname === Constants.PAGES_URL.SUPPORT;

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isSupport || widgetId) return;
    setWidgetIdError(false);
    getClientsphereWidgetId()
      .then(data => {
        if (!mounted.current) return;
        const id = data?.entity?.clientsphere_widget_id ?? null;
        setWidgetId(id);
        if (!id) setWidgetIdError(true);
      })
      .catch(() => {
        if (mounted.current) setWidgetIdError(true);
      });
  }, [isSupport, widgetId]);

  useEffect(() => {
    if (!widgetId) return;

    const styleEl = document.getElementById(STYLE_ID);
    if (!isSupport) {
      if (styleEl) styleEl.remove();
      setWidgetReady(false);
      return;
    }

    if (!styleEl) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = `
      #${CONTAINER_ID} {
        position: fixed !important;
        left: 16.4% !important;
        width: calc(100% - 16.4%) !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        height: 95vh !important;
        top: 50px !important;
        z-index: 10 !important;
        background: transparent !important;
      }
      
      #${CONTAINER_ID} #${WIDGET_INNER_ID} {
        position: relative !important;
        width: 80% !important;
        height: 100% !important;
        max-width: 80% !important;
        margin: 0 auto !important;
      }

      #${CONTAINER_ID} #${WIDGET_INNER_ID} > div {
        width: 100% !important;
        max-width: 100% !important;
      }

      @media (max-width: 1024px) {
        #${CONTAINER_ID} #${WIDGET_INNER_ID} {
          width: 95% !important;
        }
      }

      @media (max-width: 768px) {
        #${CONTAINER_ID} {
          left: 0 !important;
          width: 100% !important;
        }
        #${CONTAINER_ID} #${WIDGET_INNER_ID} {
          width: 100% !important;
        }
      }
    `;
      document.head.appendChild(style);
    }

    const constrainWidgetStyles = widget => {
      if (!widget) return;
      Object.assign(widget.style, {
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
      });
      const inner = widget.firstElementChild;
      if (inner) {
        inner.style.height = '95vh';
        inner.style.maxHeight = '95vh';
      }
    };

    const moveWidgetIntoContainer = () => {
      const widget = document.getElementById(WIDGET_INNER_ID);
      const container = document.getElementById(CONTAINER_ID);
      if (widget && container && widget.parentElement?.id !== CONTAINER_ID) {
        container.appendChild(widget);
        constrainWidgetStyles(widget);
        setWidgetReady(true);
        return true;
      }
      if (widget?.parentElement?.id === CONTAINER_ID) {
        constrainWidgetStyles(widget);
        setWidgetReady(true);
        return true;
      }
      return false;
    };

    setWidgetReady(false);
    moveWidgetIntoContainer();
    const t1 = setTimeout(moveWidgetIntoContainer, 200);
    const t2 = setTimeout(moveWidgetIntoContainer, 800);
    const t3 = setTimeout(moveWidgetIntoContainer, 2000);

    const observer = new MutationObserver(() => moveWidgetIntoContainer());
    observer.observe(document.body, {childList: true, subtree: true});

    return () => {
      observer.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [isSupport, widgetId]);

  useEffect(() => {
    if (!widgetId || !isSupport) return;
    if (
      scriptInjected.current ||
      document.querySelector(`script[src="${SCRIPT_URL}"]`)
    )
      return;
    scriptInjected.current = true;
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.setAttribute('data-widget-id', widgetId);
    script.setAttribute('data-container-id', CONTAINER_ID);
    const companyId = userDetails?.company?.id;
    const companyName = userDetails?.company?.name;
    const userId = user?.id;
    const userEmail = user?.email;
    const userName = [user?.first_name, user?.last_name]
      .filter(Boolean)
      .join(' ');
    const userAvatar = userDetails?.avatar_url || userDetails?.profile_image;
    if (companyId) script.setAttribute('data-customer-id', String(companyId));
    if (companyName) script.setAttribute('data-customer-name', companyName);
    if (userId) script.setAttribute('data-user-id', String(userId));
    if (userEmail) script.setAttribute('data-user-email', userEmail);
    if (userName) script.setAttribute('data-user-name', userName);
    if (userAvatar) script.setAttribute('data-user-avatar-url', userAvatar);
    document.body.appendChild(script);
  }, [isSupport, user, userDetails, widgetId]);

  const containerStyle = {
    display: isSupport ? 'flex' : 'none',
    position: 'fixed',
    left: '16.4%',
    top: 50,
    width: 'calc(100% - 16.4%)',
    height: '95vh',
    justifyContent: 'center',
    zIndex: 10,
  };

  return (
    <div
      id={CONTAINER_ID}
      className="relative w-full h-full bg-white overflow-hidden flex flex-col items-center justify-center"
      style={containerStyle}
    >
      {widgetIdError ? (
        <p className="text-deepBlue text-center">
          Support is currently unavailable.
        </p>
      ) : !widgetId ? (
        <Loader height={45} />
      ) : (
        isSupport &&
        !widgetReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <Loader height={45} />
          </div>
        )
      )}
    </div>
  );
}

export default ClientSphereWidget;
